import {
  useState,
  useContext,
} from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Chip, { ChipProps } from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import {
  useGetRemindersQuery,
  useCreateReminderMutation,
  useDeleteReminderMutation,
} from '#services/order';
import { formatDate } from '#common/utils';
import { FeedbackUIContext } from '#context/feedback-ui-context';

const CRITICAL_LEVELS = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
};

const CRITICAL_LEVEL_COLORS: Record<string, string> = {
  [CRITICAL_LEVELS.LOW]: 'default',
  [CRITICAL_LEVELS.MEDIUM]: 'warning',
  [CRITICAL_LEVELS.HIGH]: 'error',
};

const CRITICAL_LEVEL_LABELS: Record<string, string> = {
  [CRITICAL_LEVELS.LOW]: 'Low',
  [CRITICAL_LEVELS.MEDIUM]: 'Medium',
  [CRITICAL_LEVELS.HIGH]: 'High',
};

interface Reminder {
  _id: string;
  name: string;
  description?: string;
  criticalLevel: string;
  reminderDate: string;
  createdByName?: string;
  createdAt: string;
}

interface ReminderFormData {
  name: string;
  description: string;
  criticalLevel: string;
  reminderDate: Date;
}

export default function RemindersPanel({ orderId }: { orderId?: string | number }) {
  const { simpleSnackbar } = useContext(FeedbackUIContext)!;
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState<ReminderFormData>({
    name: '',
    description: '',
    criticalLevel: CRITICAL_LEVELS.MEDIUM,
    reminderDate: new Date(),
  });

  const { data: remindersData, refetch } = useGetRemindersQuery(
    { orderId },
    { skip: !orderId },
  ) as { data?: { reminders?: Reminder[] }; refetch: () => void };
  const [createReminder, { isLoading: isCreating }] = useCreateReminderMutation();
  const [deleteReminder, { isLoading: isDeleting }] = useDeleteReminderMutation();

  const reminders = remindersData?.reminders || [];

  const handleOpenDialog = () => {
    setFormData({
      name: '',
      description: '',
      criticalLevel: CRITICAL_LEVELS.MEDIUM,
      reminderDate: new Date(),
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleFormChange = (field: keyof ReminderFormData, value: unknown) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCreateReminder = async () => {
    if (!formData.name.trim()) {
      simpleSnackbar({ error: { data: { message: 'Please enter a reminder name' } } });
      return;
    }

    try {
      await createReminder({
        orderId,
        name: formData.name,
        description: formData.description,
        criticalLevel: formData.criticalLevel,
        reminderDate: formData.reminderDate.toISOString(),
      }).unwrap();

      simpleSnackbar({}, 'Reminder created successfully');
      handleCloseDialog();
      refetch();
    } catch (error) {
      console.error('Error creating reminder:', error);
      simpleSnackbar({ error: { data: { message: 'Failed to create reminder' } } });
    }
  };

  const handleDeleteReminder = async (reminderId: string) => {
    try {
      await deleteReminder({ reminderId }).unwrap();
      simpleSnackbar({}, 'Reminder deleted successfully');
      refetch();
    } catch (error) {
      console.error('Error deleting reminder:', error);
      simpleSnackbar({ error: { data: { message: 'Failed to delete reminder' } } });
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: 'center',
          }}
        >
          <NotificationsActiveIcon color="primary" />
          <Typography variant="h6">
            Order Reminders
          </Typography>
          {reminders.length > 0 && (
            <Chip
              label={reminders.length}
              size="small"
              color="primary"
            />
          )}
        </Stack>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
        >
          Add Reminder
        </Button>
      </Stack>
      {reminders.length === 0 ? (
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            py: 2,
            textAlign: 'center',
          }}
        >
          No reminders set for this order
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {reminders.map((reminder) => (
            <Box
              key={reminder._id}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 1.5,
              }}
            >
              <Stack
                direction="row"
                sx={{
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      alignItems: 'center',
                      mb: 0.5,
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {reminder.name}
                    </Typography>
                    <Chip
                      label={CRITICAL_LEVEL_LABELS[reminder.criticalLevel]}
                      size="small"
                      color={CRITICAL_LEVEL_COLORS[reminder.criticalLevel] as ChipProps['color']}
                    />
                  </Stack>
                  {reminder.description && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        mb: 0.5,
                      }}
                    >
                      {reminder.description}
                    </Typography>
                  )}
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    Due:
                    {' '}
                    {formatDate(reminder.reminderDate)}
                    {' · '}
                    Created by
                    {' '}
                    {reminder.createdByName || '-'}
                    {' on '}
                    {formatDate(reminder.createdAt)}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => { void handleDeleteReminder(reminder._id); }}
                  disabled={isDeleting}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Box>
          ))}
        </Stack>
      )}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add Reminder</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Reminder Name"
              fullWidth
              required
              value={formData.name}
              onChange={(e) => handleFormChange('name', e.target.value)}
              placeholder="e.g., Follow up with customer"
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => handleFormChange('description', e.target.value)}
              placeholder="Additional details about this reminder"
            />
            <TextField
              label="Critical Level"
              fullWidth
              select
              value={formData.criticalLevel}
              onChange={(e) => handleFormChange('criticalLevel', e.target.value)}
            >
              <MenuItem value={CRITICAL_LEVELS.LOW}>Low</MenuItem>
              <MenuItem value={CRITICAL_LEVELS.MEDIUM}>Medium</MenuItem>
              <MenuItem value={CRITICAL_LEVELS.HIGH}>High</MenuItem>
            </TextField>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Reminder Date"
                value={formData.reminderDate}
                onChange={(date) => handleFormChange('reminderDate', date)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => { void handleCreateReminder(); }}
            disabled={isCreating}
          >
            Create Reminder
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
